import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LookupStore } from '@core/lookup/lookup.store';
import { LOOKUP_CODES, SESSION_NOTE_LOOKUP_CODES } from '@core/lookup/lookup-codes';
import { PermissionService } from '@core/services/permission.service';
import { DateTimePickerComponent } from '@shared/components/date-time-picker/date-time-picker.component';
import { Permission } from '@shared/enums/permission.enum';
import { SHARED_IMPORTS } from '../../../../../shared/modules/shared-imports.module';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { InterventionStore } from '../../../intervention.store';
import { SessionNoteRequest, SessionNoteResponse } from '../../../intervention.model';
import { nullIfBlank } from '@shared/utils/payload-utils';

const MAX_IMPACT_ROWS = 20;
const MAX_OUT_OF_SCOPE_ROWS = 5;

type ImpactRow = FormGroup<{ functioningAreaId: FormControl<string>; description: FormControl<string>; rating: FormControl<number | null> }>;
type OutOfScopeRow = FormGroup<{ problem: FormControl<string>; intervention: FormControl<string>; resource: FormControl<string> }>;

// The Formal EAP Counselling clinical case notes of one attended session (MW-162). The notes of the first attended
// session also carry the goals discussion, the baseline functioning assessment, the out-of-scope problems and the
// initial number of sessions; later sessions are the shorter form. What the forms mark as duplicates of the workflow
// (case number, counsellor, date, venue, referral type) is shown from the case, not asked for again.
@Component({
  selector: 'app-session-note-page',
  imports: [DatePipe, RouterLink, ReactiveFormsModule, DateTimePickerComponent, ...SharedMaterialUIModule, ...SHARED_IMPORTS],
  templateUrl: './session-note.page.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SessionNotePage {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly lookupStore = inject(LookupStore);
  private readonly permissionService = inject(PermissionService);
  protected readonly store = inject(InterventionStore);

  protected readonly interventionId = this.route.snapshot.paramMap.get('id') ?? '';
  protected readonly sessionId = this.route.snapshot.paramMap.get('sessionId') ?? '';

  protected readonly scale = [0, 1, 2, 3, 4, 5];
  protected readonly initialSessionOptions = [
    { value: 0, label: 'N/A (case closure at the 1st session)' },
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
  ];
  protected readonly maxImpactRows = MAX_IMPACT_ROWS;
  protected readonly maxOutOfScopeRows = MAX_OUT_OF_SCOPE_ROWS;

  protected readonly impacts = new FormArray<ImpactRow>([]);
  protected readonly outOfScope = new FormArray<OutOfScopeRow>([]);

  protected readonly form = this.formBuilder.nonNullable.group({
    goalsDiscussed: this.formBuilder.control<boolean | null>(null),
    goalsAgreed: this.formBuilder.control<boolean | null>(null),
    goalsNotDiscussedReason: [''],
    problemSeverity: this.formBuilder.control<number | null>(null),
    copingLevel: this.formBuilder.control<number | null>(null),
    riskRatingId: this.formBuilder.control<string | null>(null),
    interventionsProvided: [''],
    homeworkGiven: [''],
    commitmentLevel: this.formBuilder.control<number | null>(null),
    clinicalImpression: [''],
    processRisks: [''],
    nextSteps: [''],
    initialSessionsRequired: this.formBuilder.control<number | null>(null),
    nextSessionDate: [''],
    nextSessionReason: [''],
    additionalComments: [''],
    earlyTermination: this.formBuilder.control<boolean | null>(null),
    motivationForAdditionalSessions: this.formBuilder.control<boolean | null>(null),
  });

  protected readonly session = computed(() => this.store.sessions().find((item) => item.id === this.sessionId) ?? null);
  protected readonly note = computed<SessionNoteResponse | null>(
    () => this.store.sessionNotes().find((item) => item.sessionId === this.sessionId) ?? null,
  );
  protected readonly intervention = computed(() => this.store.selected());

  protected readonly typeName = computed(
    () => this.store.interventionTypes().find((type) => type.id === this.intervention()?.interventionTypeId)?.name ?? '',
  );

  // The server decides, from the first time the notes are written; the form shows the kind that will apply.
  protected readonly isFirst = computed(() => {
    const note = this.note();
    if (note) {
      return note.noteType === 'First';
    }

    const session = this.session();
    return !!session && !this.store.sessions().some((item) => item.status === 'Attended' && item.sessionNumber < session.sessionNumber);
  });

  protected readonly riskRatings = computed(() => this.lookupStore.items(LOOKUP_CODES.riskRating));
  protected readonly functioningAreas = computed(() => this.lookupStore.items(LOOKUP_CODES.functioningArea));

  protected readonly canEdit = computed(
    () => this.permissionService.hasPermission(Permission.SessionsRecord) && this.intervention()?.status !== 'Closed',
  );

  protected readonly venue = computed(() => {
    const session = this.session();
    return [session?.counsellingMediumName, session?.location].filter(Boolean).join(' · ');
  });

  private populated = false;

  constructor() {
    this.lookupStore.load(SESSION_NOTE_LOOKUP_CODES);
    this.store.loadInterventionTypes();
    this.store.loadOne(this.interventionId);
    this.store.loadSessions(this.interventionId);
    this.store.loadSessionNotes(this.interventionId);
    this.store.loadReferralSummary(this.interventionId);

    effect(() => {
      const note = this.note();
      if (note && !this.populated) {
        this.populated = true;
        this.populate(note);
      }

      if (this.canEdit()) {
        this.form.enable({ emitEvent: false });
        this.impacts.enable({ emitEvent: false });
        this.outOfScope.enable({ emitEvent: false });
      } else {
        this.form.disable({ emitEvent: false });
        this.impacts.disable({ emitEvent: false });
        this.outOfScope.disable({ emitEvent: false });
      }
    });
  }

  protected addImpact(): void {
    if (this.impacts.length < MAX_IMPACT_ROWS) {
      this.impacts.push(this.newImpact('', '', null));
    }
  }

  protected addOutOfScope(): void {
    if (this.outOfScope.length < MAX_OUT_OF_SCOPE_ROWS) {
      this.outOfScope.push(this.newOutOfScope('', '', ''));
    }
  }

  // The form says to give a reason when the next session is more than a week after this one.
  protected needsDelayReason(): boolean {
    const next = this.form.controls.nextSessionDate.value;
    const session = this.session();
    if (!next || !session) {
      return false;
    }

    const limit = new Date(session.sessionDate.slice(0, 16)).getTime() + 7 * 24 * 60 * 60 * 1000;
    return new Date(next).getTime() > limit;
  }

  // Mirrors the server: everything the form insists on is answered, including the first-session parts.
  protected canSave(): boolean {
    const v = this.form.getRawValue();
    const text = (value: string) => !!value.trim();

    const everySession =
      v.copingLevel !== null &&
      v.commitmentLevel !== null &&
      !!v.riskRatingId &&
      text(v.interventionsProvided) &&
      v.earlyTermination !== null &&
      v.motivationForAdditionalSessions !== null &&
      (!this.needsDelayReason() || text(v.nextSessionReason));

    const goals = v.goalsDiscussed === true ? v.goalsAgreed !== null : v.goalsDiscussed === false && text(v.goalsNotDiscussedReason);
    const impactsValid = this.impacts.getRawValue().every((row) => !!row.functioningAreaId && text(row.description) && row.rating !== null);
    const outOfScopeValid = this.outOfScope.getRawValue().every((row) => text(row.problem) || (!row.intervention.trim() && !row.resource.trim()));

    const first = !this.isFirst() || (v.problemSeverity !== null && goals && v.initialSessionsRequired !== null && impactsValid && outOfScopeValid);

    return this.canEdit() && everySession && first && !this.store.savingSessionNote();
  }

  protected save(): void {
    if (!this.canSave()) {
      return;
    }

    const v = this.form.getRawValue();
    const text = nullIfBlank;
    const first = this.isFirst();

    const request: SessionNoteRequest = {
      copingLevel: v.copingLevel,
      riskRatingId: v.riskRatingId,
      interventionsProvided: text(v.interventionsProvided),
      homeworkGiven: text(v.homeworkGiven),
      commitmentLevel: v.commitmentLevel,
      clinicalImpression: text(v.clinicalImpression),
      processRisks: text(v.processRisks),
      nextSteps: text(v.nextSteps),
      nextSessionDate: v.nextSessionDate ? `${v.nextSessionDate}:00` : null,
      nextSessionReason: text(v.nextSessionReason),
      earlyTermination: v.earlyTermination,
      additionalComments: text(v.additionalComments),
      motivationForAdditionalSessions: v.motivationForAdditionalSessions,
      problemSeverity: first ? v.problemSeverity : null,
      goalsDiscussed: first ? v.goalsDiscussed : null,
      goalsAgreed: first && v.goalsDiscussed ? v.goalsAgreed : null,
      goalsNotDiscussedReason: first && v.goalsDiscussed === false ? text(v.goalsNotDiscussedReason) : null,
      initialSessionsRequired: first ? v.initialSessionsRequired : null,
      impactAreas: first
        ? this.impacts
            .getRawValue()
            .map((row) => ({ functioningAreaId: row.functioningAreaId, description: row.description.trim(), rating: row.rating ?? 0 }))
        : [],
      outOfScopeProblems: first
        ? this.outOfScope
            .getRawValue()
            .filter((row) => row.problem.trim() || row.intervention.trim() || row.resource.trim())
            .map((row) => ({ problem: row.problem.trim(), intervention: text(row.intervention), resource: text(row.resource) }))
        : [],
    };

    this.store.saveSessionNote(this.interventionId, this.sessionId, request, () =>
      void this.router.navigate(['/interventions', this.interventionId]),
    );
  }

  private populate(note: SessionNoteResponse): void {
    this.form.patchValue(
      {
        goalsDiscussed: note.goalsDiscussed,
        goalsAgreed: note.goalsAgreed,
        goalsNotDiscussedReason: note.goalsNotDiscussedReason ?? '',
        problemSeverity: note.problemSeverity,
        copingLevel: note.copingLevel,
        riskRatingId: note.riskRatingId,
        interventionsProvided: note.interventionsProvided ?? '',
        homeworkGiven: note.homeworkGiven ?? '',
        commitmentLevel: note.commitmentLevel,
        clinicalImpression: note.clinicalImpression ?? '',
        processRisks: note.processRisks ?? '',
        nextSteps: note.nextSteps ?? '',
        initialSessionsRequired: note.initialSessionsRequired,
        nextSessionDate: note.nextSessionDate ? note.nextSessionDate.slice(0, 16) : '',
        nextSessionReason: note.nextSessionReason ?? '',
        additionalComments: note.additionalComments ?? '',
        earlyTermination: note.earlyTermination,
        motivationForAdditionalSessions: note.motivationForAdditionalSessions,
      },
      { emitEvent: false },
    );

    this.impacts.clear();
    for (const row of note.impactAreas) {
      this.impacts.push(this.newImpact(row.functioningAreaId, row.description, row.rating));
    }

    this.outOfScope.clear();
    for (const row of note.outOfScopeProblems) {
      this.outOfScope.push(this.newOutOfScope(row.problem, row.intervention ?? '', row.resource ?? ''));
    }
  }

  private newImpact(functioningAreaId: string, description: string, rating: number | null): ImpactRow {
    return this.formBuilder.nonNullable.group({
      functioningAreaId: [functioningAreaId],
      description: [description],
      rating: this.formBuilder.control<number | null>(rating),
    });
  }

  private newOutOfScope(problem: string, intervention: string, resource: string): OutOfScopeRow {
    return this.formBuilder.nonNullable.group({ problem: [problem], intervention: [intervention], resource: [resource] });
  }
}
